import { ContainerBodyProps } from '@chronosphereio/chrono-ui';
import { ErrorBoundary, ErrorBoundaryProps } from '@/components/ErrorBoundary';
import { VizContainerContentError } from '../content-error/VizContainerContentError';
import { VizContainerBodyBase } from './VizContainerBodyBase';

export interface VizContainerBodyProps extends ContainerBodyProps {
  /**
   * Props for configuring the error boundary. If not set, it will default to using `VizContainer.ContentError` as a
   * `FallbackComponent` with no other configuration.
   */
  errorBoundaryProps?: ErrorBoundaryProps;
}

// We only set a fallback component by default because people can render pretty much anything inside the body, so we
// cannot be prescriptive about reset behavior without potentially causing infinite loops or other problematic
// behavior.
const DEFAULT_ERROR_BOUNDARY_FALLBACK: ErrorBoundaryProps = {
  FallbackComponent: VizContainerContentError,
};

/**
 * Component that contains content to be rendered within a `VizContainer`.
 *
 * It extends from the chrono-ui `Container.Body` component and shares similar props and behavior, when possible,
 * for consistency.
 *
 * This is the "enhanced" version of the component that automatically comes with built-in features. Instead use
 * `BodyBase` if you want a much simpler component. Built-in features currently include:
 * - Automatic wrapping in an error boundary.
 */
export const VizContainerBody = ({
  children,
  errorBoundaryProps = DEFAULT_ERROR_BOUNDARY_FALLBACK,
  ...containerProps
}: VizContainerBodyProps) => {
  return (
    <VizContainerBodyBase {...containerProps}>
      <ErrorBoundary {...errorBoundaryProps}>{children}</ErrorBoundary>
    </VizContainerBodyBase>
  );
};
